import { isSameOrderItemId } from "./tableCartSync";

export const getExplicitOrderItemId = (item) => {
  const value = item?.order_item_id ?? item?.orderItemId ?? null;
  if (value === null || value === undefined || value === "") return null;
  return value;
};

export const hasCanonicalEmptyTableSnapshot = (table = {}) => {
  const hasActiveCount =
    Object.prototype.hasOwnProperty.call(table, "active_items_count") ||
    Object.prototype.hasOwnProperty.call(table, "activeItemsCount");
  const activeCount = Number(
    table.active_items_count ?? table.activeItemsCount ?? 0,
  );
  return (
    String(table.status || "").toLowerCase() === "free" &&
    hasActiveCount &&
    Number.isFinite(activeCount) &&
    activeCount === 0
  );
};

// Une réponse canonique vide invalide les anciens articles déjà envoyés du
// cache d'un autre poste. Seuls les vrais brouillons locaux restent capables
// de maintenir la table occupée jusqu'à leur envoi ou leur annulation explicite.
export const resolveLocalCartOccupancyCount = (
  table = {},
  signal = {},
) => {
  if (hasCanonicalEmptyTableSnapshot(table)) {
    // Le serveur ne connaît pas les lignes encore en brouillon sur ce terminal.
    // Un snapshot canonique vide peut invalider d'anciens articles envoyés,
    // jamais un brouillon local actif, quelle que soit son ancienneté.
    return Number(signal.pendingCount || 0);
  }
  return Number(signal.activeCount || 0);
};

export const shouldClearCanonicalEmptyLocalCart = ({
  table = {},
  signal = {},
  hasRecentLocalMutation = false,
} = {}) =>
  hasCanonicalEmptyTableSnapshot(table) &&
  !hasRecentLocalMutation &&
  Number(signal.pendingCount || 0) <= 0 &&
  Number(signal.existingCount || 0) > 0;

const hasCourseIdentity = (item) => {
  const courseId = item?.course_id ?? item?.courseId ?? null;
  const courseNumber = Number(item?.course_number ?? item?.courseNumber);
  return (
    (courseId !== null && courseId !== undefined && courseId !== "") ||
    (Number.isInteger(courseNumber) && courseNumber > 1)
  );
};

// La valeur `false` d'un ancien cache ne doit jamais déclasser une commande
// multi-tour déjà enregistrée. Les cours et les articles portent une preuve
// métier plus fiable que ce booléen d'interface.
export const resolveCartCourseManagement = ({
  explicitEnabled = false,
  courses = [],
  pendingItems = [],
  existingItems = [],
} = {}) =>
  Boolean(explicitEnabled) ||
  (Array.isArray(courses) && courses.length > 0) ||
  [...(Array.isArray(pendingItems) ? pendingItems : []),
    ...(Array.isArray(existingItems) ? existingItems : []),
  ].some(hasCourseIdentity);

export const reconcileExistingItemsWithSnapshot = (
  cachedItems = [],
  serverItems = [],
) => {
  const authoritativeItems = Array.isArray(serverItems) ? serverItems : [];
  const cached = Array.isArray(cachedItems) ? cachedItems : [];
  const cachedByItemId = new Map(
    cached
      .map((item) => [getExplicitOrderItemId(item), item])
      .filter(([itemId]) => itemId !== null)
      .map(([itemId, item]) => [String(itemId), item]),
  );
  const itemsWithPreservedCourseIdentity = authoritativeItems.map((item) => {
    const itemId = getExplicitOrderItemId(item);
    const cachedItem = itemId === null ? null : cachedByItemId.get(String(itemId));
    if (!cachedItem) return item;

    const serverCourseId = item?.course_id ?? item?.courseId ?? null;
    const serverCourseNumber = item?.course_number ?? item?.courseNumber ?? null;
    const cachedCourseId = cachedItem?.course_id ?? cachedItem?.courseId ?? null;
    const cachedCourseNumber =
      cachedItem?.course_number ?? cachedItem?.courseNumber ?? null;
    if (serverCourseId !== null && serverCourseNumber !== null) return item;
    if (cachedCourseId === null && cachedCourseNumber === null) return item;

    return {
      ...item,
      course_id: serverCourseId ?? cachedCourseId,
      courseId: serverCourseId ?? cachedCourseId,
      course_number: serverCourseNumber ?? cachedCourseNumber,
      courseNumber: serverCourseNumber ?? cachedCourseNumber,
    };
  });
  const anomalousItems = cached.filter(
    (item) => getExplicitOrderItemId(item) === null,
  );
  const removedItemIds = cached
    .map(getExplicitOrderItemId)
    .filter(
      (itemId) =>
        itemId !== null &&
        !itemsWithPreservedCourseIdentity.some((item) =>
          isSameOrderItemId(getExplicitOrderItemId(item), itemId),
        ),
    );

  return {
    // Unidentified cached rows are preserved separately, never promoted to
    // authoritative server items merely because a cache once called them sent.
    items: itemsWithPreservedCourseIdentity,
    anomalousItems,
    removedItemIds,
  };
};

export const snapshotHasCanonicalItems = (snapshot) => {
  if (!snapshot?.complete || !Array.isArray(snapshot?.orders)) return false;
  const tableStatus = String(snapshot?.table_status || "").toLowerCase();
  if (!["free", "occupied", "reserved", "maintenance"].includes(tableStatus)) {
    return false;
  }
  const items = snapshot.orders.flatMap((order) =>
    Array.isArray(order?.items) ? order.items : [],
  );
  const declaredCount = Number(snapshot.active_items_count);
  const coursedOrdersHaveCompleteIdentity = snapshot.orders.every((order) => {
    if (Number(order?.course_management_enabled || 0) !== 1) return true;
    return (order?.items || []).every((item) => {
      const courseId = item?.course_id ?? item?.courseId ?? null;
      const courseNumber = Number(item?.course_number ?? item?.courseNumber);
      return (
        courseId !== null &&
        courseId !== "" &&
        Number.isInteger(courseNumber) &&
        courseNumber > 0
      );
    });
  });
  return (
    Number.isInteger(declaredCount) &&
    declaredCount >= 0 &&
    items.length === declaredCount &&
    items.every((item) => getExplicitOrderItemId(item) !== null) &&
    coursedOrdersHaveCompleteIdentity
  );
};

export const addServerCustomerCountIfMissing = (
  table,
  tableId,
  serverCustomerCount,
) => {
  if (!table || String(table.id) !== String(tableId)) return table;
  const localCount = table.customerCount ?? table.customer_count;
  if (localCount !== null && localCount !== undefined && localCount !== "") {
    return table;
  }
  if (serverCustomerCount === null || serverCustomerCount === undefined) {
    return table;
  }
  return {
    ...table,
    customerCount: serverCustomerCount,
    customer_count: serverCustomerCount,
  };
};

export const canReleaseTableFromSnapshot = ({
  tableStatus,
  shouldClear,
  isVisible = false,
  visibleDoubleConfirmed = false,
} = {}) =>
  Boolean(shouldClear) &&
  String(tableStatus || "").toLowerCase() === "free" &&
  (!isVisible || Boolean(visibleDoubleConfirmed));

export const buildTableCartReconciliationState = ({
  cachedExistingItems = [],
  serverItems = [],
  pendingItems = [],
  activeItemsCount = 0,
  hadSentItemAtStart = false,
} = {}) => {
  const reconciliation = reconcileExistingItemsWithSnapshot(
    cachedExistingItems,
    serverItems,
  );
  const preservedPendingItems = Array.isArray(pendingItems) ? pendingItems : [];
  const serverCount = Number(activeItemsCount);
  const shouldPurgeSentItems =
    serverCount === 0 && Boolean(hadSentItemAtStart);
  return {
    ...reconciliation,
    pendingItems: preservedPendingItems,
    shouldPurgeSentItems,
    shouldClear:
      shouldPurgeSentItems &&
      preservedPendingItems.length === 0 &&
      reconciliation.items.length === 0,
  };
};
